import { useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import {
  PREVIEW_POLL_INTERVAL_MS,
  PREVIEW_POLL_TIMEOUT_MS,
} from './preview-limits'
import type { PreviewSource } from './preview-content'
import type { DocumentPreviewTargetInput } from '@/graphql/schema'
import type {
  DocumentPreviewQuery,
  DocumentPreviewQueryVariables,
} from '@/graphql/queries/documentPreviewQuery.gql.generated'
import type {
  RequestDocumentPreviewMutation,
  RequestDocumentPreviewMutationVariables,
} from '@/graphql/mutations/requestDocumentPreviewMutation.gql.generated'
import {
  DocumentPreviewRenderer,
  DocumentPreviewStatus,
} from '@/graphql/schema'
import { DocumentPreviewDocument } from '@/graphql/queries/documentPreviewQuery.gql.generated'
import { RequestDocumentPreviewDocument } from '@/graphql/mutations/requestDocumentPreviewMutation.gql.generated'

export type PreviewFailure =
  | 'unsupported'
  | 'tooLarge'
  | 'encrypted'
  | 'malformed'
  | 'unavailable'
  | 'generic'

export type PreviewState =
  | { phase: 'loading' }
  | {
      phase: 'ready'
      renderer: DocumentPreviewRenderer
      fileName: string
      source: PreviewSource
    }
  | { phase: 'failed'; reason: PreviewFailure }

const FAILURE_REASONS: Partial<Record<string, PreviewFailure>> = {
  unsupported_format: 'unsupported',
  document_too_large: 'tooLarge',
  document_encrypted: 'encrypted',
  document_malformed: 'malformed',
  conversion_unavailable: 'unavailable',
  conversion_timeout: 'unavailable',
  workspace_read_only: 'unavailable',
  storage_quota_exceeded: 'unavailable',
}

export function failureReason(code: string | null | undefined): PreviewFailure {
  return (code && FAILURE_REASONS[code]) || 'generic'
}

type Preview = DocumentPreviewQuery['documentPreview']

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

function toState(
  preview: Preview,
  refresh: () => Promise<string>,
): PreviewState | null {
  if (preview.status === DocumentPreviewStatus.Ready && preview.url)
    return {
      phase: 'ready',
      renderer: preview.renderer ?? DocumentPreviewRenderer.Text,
      fileName: preview.fileName,
      source: { url: preview.url, refresh },
    }
  if (
    preview.status === DocumentPreviewStatus.Failed ||
    preview.status === DocumentPreviewStatus.Unsupported
  )
    return { phase: 'failed', reason: failureReason(preview.failureCode) }

  return null
}

/**
 * Asks for the preview only while a target is open and keeps the signed URL in
 * component state alone: never in the Apollo cache, a transcript or storage.
 * State belongs to one mounted target; remount (key) to preview another.
 */
export function useDocumentPreview(
  target: DocumentPreviewTargetInput,
): PreviewState {
  const client = useApolloClient()
  const [state, setState] = useState<PreviewState>({ phase: 'loading' })
  const { kind, id } = target

  useEffect(() => {
    const variables = { target: { kind, id } }
    const lifecycle = new AbortController()
    const query = async (): Promise<Preview> => {
      const { data } = await client.query<
        DocumentPreviewQuery,
        DocumentPreviewQueryVariables
      >({ query: DocumentPreviewDocument, variables, fetchPolicy: 'no-cache' })

      if (!data) throw new Error('Preview unavailable')

      return data.documentPreview
    }
    const refresh = async () => {
      const next = await query()
      if (next.status !== DocumentPreviewStatus.Ready || !next.url)
        throw new Error('Preview access is no longer available')

      return next.url
    }
    const run = async () => {
      const { data } = await client.mutate<
        RequestDocumentPreviewMutation,
        RequestDocumentPreviewMutationVariables
      >({
        mutation: RequestDocumentPreviewDocument,
        variables,
        fetchPolicy: 'no-cache',
      })
      if (!data) return setState({ phase: 'failed', reason: 'generic' })
      let preview = data.requestDocumentPreview
      const deadline = Date.now() + PREVIEW_POLL_TIMEOUT_MS
      while (!lifecycle.signal.aborted) {
        const settled = toState(preview, refresh)
        if (settled) return setState(settled)
        if (Date.now() > deadline)
          return setState({ phase: 'failed', reason: 'unavailable' })
        await delay(PREVIEW_POLL_INTERVAL_MS)
        preview = await query()
      }
    }
    run().catch(() => {
      if (!lifecycle.signal.aborted)
        setState({ phase: 'failed', reason: 'generic' })
    })

    return () => {
      lifecycle.abort()
    }
  }, [client, kind, id])

  return state
}
